import { z } from 'zod';

export const MaintenanceJobStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
]);
export type MaintenanceJobStatus = z.infer<typeof MaintenanceJobStatusSchema>;

/** How the generic run form should render one input field. */
export const JobFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.enum(['text', 'number', 'boolean', 'multi-select']),
  help: z.string().optional(),
  placeholder: z.string().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
});
export type JobField = z.infer<typeof JobFieldSchema>;

/** A job the server knows how to run, as advertised to the admin UI. */
export const JobTypeSchema = z.object({
  type: z.string(),
  label: z.string(),
  description: z.string(),
  defaultInput: z.record(z.unknown()),
  fields: z.array(JobFieldSchema),
  singleton: z.boolean(),
  /** Whether the calling user may run it — the UI disables rather than hides. */
  canRun: z.boolean(),
  /** True when a singleton job of this type is already pending or processing. */
  isRunning: z.boolean(),
});
export type JobType = z.infer<typeof JobTypeSchema>;

export const MaintenanceJobSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: MaintenanceJobStatusSchema,
  input: z.record(z.unknown()),
  result: z.unknown().nullable(),
  progress: z.number().int().min(0).max(100),
  progressMessage: z.string().nullable(),
  errorMessage: z.string().nullable(),
  cancelRequested: z.boolean(),
  requestedByUid: z.string().nullable(),
  startedAt: z.coerce.date().nullable(),
  finishedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type MaintenanceJob = z.infer<typeof MaintenanceJobSchema>;

export const SubmitMaintenanceJobSchema = z.object({
  type: z.string().min(1),
  input: z.record(z.unknown()).optional(),
});
export type SubmitMaintenanceJobInput = z.infer<typeof SubmitMaintenanceJobSchema>;

export const MaintenanceJobFiltersSchema = z.object({
  type: z.string().optional(),
  status: MaintenanceJobStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(25),
  offset: z.coerce.number().int().min(0).default(0),
});
export type MaintenanceJobFilters = z.infer<typeof MaintenanceJobFiltersSchema>;

export const MaintenanceJobListResponseSchema = z.object({
  items: z.array(MaintenanceJobSchema),
  total: z.number().int().min(0),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type MaintenanceJobListResponse = z.infer<typeof MaintenanceJobListResponseSchema>;

// ─── Duplicate detection ──────────────────────────────────────────────────────

export const DuplicateStrategySchema = z.enum(['source-url', 'acoustic', 'title-artist']);
export type DuplicateStrategy = z.infer<typeof DuplicateStrategySchema>;

/** A song as shown in the duplicate review UI, snapshotted when the scan ran. */
export const DuplicateSongSchema = z.object({
  id: z.string(),
  title: z.string(),
  artist: z.string(),
  durationSeconds: z.number().optional(),
  coverArtUrl: z.string().optional(),
  sourceUrl: z.string().optional(),
  tags: z.array(z.string()),
  addedByDisplayName: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type DuplicateSong = z.infer<typeof DuplicateSongSchema>;

export const DuplicateCandidateSchema = z.object({
  songIdA: z.string(),
  songIdB: z.string(),
  strategy: DuplicateStrategySchema,
  confidence: z.number(),
  reason: z.string(),
});

export const DuplicateGroupSchema = z.object({
  songs: z.array(DuplicateSongSchema),
  candidates: z.array(DuplicateCandidateSchema),
  topConfidence: z.number(),
});
export type DuplicateGroup = z.infer<typeof DuplicateGroupSchema>;

export const FindDuplicatesResultSchema = z.object({
  groups: z.array(DuplicateGroupSchema),
  scanned: z.number(),
  /** Songs the scan could not consider for want of an audio fingerprint. */
  unfingerprinted: z.number(),
  dismissedPairsSkipped: z.number(),
});
export type FindDuplicatesResult = z.infer<typeof FindDuplicatesResultSchema>;

export const DismissDuplicateSchema = z.object({
  songIdA: z.string().min(1),
  songIdB: z.string().min(1),
});
export type DismissDuplicateInput = z.infer<typeof DismissDuplicateSchema>;

export const ResolveDuplicateGroupSchema = z.object({
  /** The song to keep. */
  keepSongId: z.string().min(1),
  /** Songs to delete. Must not contain `keepSongId`. */
  deleteSongIds: z.array(z.string().min(1)).min(1),
  /** Union the deleted songs' tags into the kept one before removing them. */
  mergeTags: z.boolean().default(false),
});
export type ResolveDuplicateGroupInput = z.infer<typeof ResolveDuplicateGroupSchema>;
